import { RouterRootState } from "connected-react-router";
import React, { FC, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { EXPORT_FORMATS } from "../../constants/system-constants";
import { TranslationKeys } from "../../contexts/TranslationKeys";
import { useLocale } from "../../contexts/localeContext";
import { useFilter } from "../../contexts/table/FilterContext";
import { useTableLegends } from "../../contexts/table/LegendContext";
import { usePagination } from "../../contexts/table/PaginationContext";
import { useSearch } from "../../contexts/table/SearchContext";
import { useSort } from "../../contexts/table/SortContext";
import { useTable } from "../../contexts/table/TableContext";
import { TableColumn } from "../../models/table/TableColumn";
import ActivityFrame from "../activity-frame/activity-frame";
import { Button } from "../button/Button";
import FilterChips from "../filter-chips/filter-chips";
import FilterModal from "../filter-modal/filter-modal";
import Paginator from "../paginator/paginator";
import SearchBar from "../search-bar/search-bar";
import Table from "../table/table";
import TableLegend from "../table/table-legend/table-legend";
import { wrapName } from "../table/table-legend/table-state-wrapper";
import SectionHeader from "../headers/section-header";

type Props = {
    title: string;
    infoText?: string;
    selectedIds?: string[];
    setSelectedIds?: (selectedIds: string[]) => void;
    selectRows?: boolean;
    rowPath?: string;
    reportTitle?: string;
    customReportDescription?: string,
    onRowClicked?: (rowData: any) => void,
    onTagSearchOpen?: () => void,
    isPaginationSupported?: boolean,
    eventThumbnails?: any,
    customHighlightRow?: any,
    tableId?: string,
    showFilterMenu?: boolean,
    customRowColourWithRange?: any,
    disableFilterToggling?: boolean,
    style?: React.CSSProperties,
    isStriped?: boolean,
    useGroupedRows?: boolean,
    tableHeight?: string,
    overflowScroll?: boolean,
    cyName: string,
    childTableHeading?: string,
    highlightExpandedParent?: boolean,
    /**
     * This function is dependent on setSelectedIds being set, if is undefined this function will not be called.
     * This is so that we don't have alot of approach to manipulate the row which can be confusing. The function
     * is mainly to get the full row data of the selected rows.
     */
    setSelectedRows?: (rows: any[]) => void;
    selectedRows?: any[];
    onCardProvision?: () => void;
}
const getRouteNameFromPath = (path: string) => {
    const splitPath = path.split('/');
    return splitPath[splitPath.length - 1];
}
const TableTab: FC<Props> = ({ title,
    infoText,
    selectedIds = [],
    setSelectedIds = () => { },
    selectRows = true,
    rowPath = "",
    reportTitle = "",
    customReportDescription = "",
    children,
    onRowClicked,
    onTagSearchOpen,
    isPaginationSupported = true,
    eventThumbnails,
    customHighlightRow,
    tableId = undefined,
    showFilterMenu = false,
    customRowColourWithRange,
    disableFilterToggling = false,
    style,
    isStriped,
    useGroupedRows = false,
    setSelectedRows = undefined,
    cyName = title,
    tableHeight,
    overflowScroll = false,
    selectedRows = undefined,
    onCardProvision,
    highlightExpandedParent = undefined,
    childTableHeading = undefined
}) => {
    const routeName = useSelector((state: RouterRootState) => getRouteNameFromPath(state.router.location.pathname));
    const legendContext = useTableLegends();
    const tableContext = useTable();
    const filterContext = useFilter();
    const searchContext = useSearch();
    const sortContext = useSort();
    const paginationContext = usePagination();
    const { translate } = useLocale();
    const [showFilterModal, setShowFilterModal] = useState(false);
    const [filterModalTrigger, setFilterModalTrigger] = useState<string>('');

    const toggleFilterModal = (trigger: string) => {
        if (!disableFilterToggling) {
            setFilterModalTrigger(trigger);
            setShowFilterModal(!showFilterModal);
        }
    };

    useEffect(() => {
        const titleToRegister = reportTitle || title;
        if (titleToRegister && tableContext.setReportTitle) {
            tableContext.setReportTitle(titleToRegister);
        }
    }, [reportTitle, title]);

    const checkReport = (event) => {
        const checkReportTitle = reportTitle ? reportTitle : title;
        const checkCustomReportDescription = customReportDescription ? customReportDescription : title;
        if ((event.value === 'expanded_pdf' || event.value === 'expanded_xls') && tableContext.exportVisibleData) {
            const format = event.value === 'expanded_pdf' ? EXPORT_FORMATS.PDF : EXPORT_FORMATS.XLS;
            tableContext.exportVisibleData(format);
            return;
        }
        tableContext.exportData(event, checkReportTitle, checkCustomReportDescription)
    }

    const onSelection = (ids) => {
        const newSelectedIds: string[] = selectedIds.map((id) => ids.includes(id) ? "" : id)
            .filter((id) => id.length > 0)
            .concat(ids.map((id) => selectedIds.includes(id) ? "" : id).filter((id) => id.length > 0))

        if (setSelectedRows && selectedRows) {
            const newRows = tableContext.tableData.filter((row) => newSelectedIds.includes(row.id.toString()));
            const rows = selectedRows.filter((row) => newSelectedIds.includes(row.id.toString()));
            newRows.forEach((row) => {
                if (row.id && !rows.find(r => r.id === row.id)) {
                    rows.push(row);
                }
            });

            setSelectedRows(rows);
        }

        setSelectedIds(newSelectedIds);
    }

    const mapRowForDisplay = (row: any) => ({
        ...row,
        _raw: row,
        name: wrapName(
            row,
            { rowPath: { path: rowPath, routeName: routeName } },
            legendContext.legendItems,
            translate.byKey(TranslationKeys.defaultRecord)
        ),
    });

    const getTableJSX = (): JSX.Element => {
        return <div className="flex w-full min-h-0 mb-3">
            <Table
                cyTableName={cyName}
                selectRows={selectRows}
                data={tableContext.tableData.map((row) => mapRowForDisplay(row))}
                groupedData={useGroupedRows
                    ? tableContext.groupedTableData?.map((group) => ({
                        ...group,
                        parentRow: mapRowForDisplay(group.parentRow),
                        childRows: group.childRows.map((row) => mapRowForDisplay(row))
                    }))
                    : []}
                columns={tableContext.tableDescriptor}
                customise
                onSort={sortContext.updateDirection}
                selectedIds={selectedIds}
                onSelectionUpdated={onSelection}
                onRowClicked={onRowClicked}
                customHighlightRow={customHighlightRow}
                tableId={tableId}
                customRowColourWithRange={customRowColourWithRange}
                rowPath={rowPath}
                isStriped={isStriped}
                childTableHeading={childTableHeading}
                highlightExpandedParent={highlightExpandedParent}/>
        </div>
    }

    return (
        <div className="w-full h-full min-h-0 flex flex-col overflow-hidden" style={style ? style : {}}>
            <div className="flex flex-row w-full justify-between flex-shrink-0">
                <div className="w-full flex flex-col">
                    <SectionHeader title={title} infoText={infoText} />
                    {filterContext.filters.filter((filter) => filter.showFilterChips !== false).length > 0 ?
                        <div className="flex flex-row mb-3">
                            <span className="mr-2 text-sm">{translate.byKey(TranslationKeys.filters)}:</span>
                            <FilterChips
                                toggleFilterModal={toggleFilterModal}
                                filters={filterContext.filters}
                                disableFilterToggling={disableFilterToggling}
                            >
                            </FilterChips>
                        </div>
                        : null}
                    <SearchBar
                        cyName={cyName}
                        newSearchTerm={searchContext.searchTerm}
                        setSearchTerm={searchContext.updateSearchTerm}
                        setSearchColumn={searchContext.updateSearchColumn}
                        toggleFilterModal={toggleFilterModal}
                        columns={searchContext.searchDescriptor}
                        generateReport={(event) => checkReport(event)}
                        onTagSearchOpen={onTagSearchOpen}
                        showFilterMenu={showFilterMenu}
                        onCardProvision={onCardProvision}>
                        {tableContext.loadDataOnDemand ? <Button variant="submit" shape="rounded" className="py-1 px-5 ml-8" onClick={() => {tableContext.setRequestToLoadDataOnDemand(true)}}>{tableContext.runDataOnDemandButtonLabel}</Button> : null}
                        {children}
                    </SearchBar>
                </div>
                <div className="flex justify-end">
                    {eventThumbnails}
                </div>
            </div>
            <ActivityFrame busy={tableContext.busy} className={`w-full flex-1 min-h-0 ${tableContext.busy ? 'mt-40' : overflowScroll ? 'overflow-auto' : 'overflow-hidden'}`} tableHeight={tableHeight ? tableHeight : ''}>
                {isPaginationSupported ? <Paginator
                        isTable
                        itemCount={tableContext.tableData.length}
                        style={{ height: '100%' }}
                        paginationObj={paginationContext.pagination}
                        onPageNumberUpdated={paginationContext.updatePageNumber}
                        onPageSizeUpdated={paginationContext.updatePageSize}
                        sizes={paginationContext.pagination.sizes}>
                        {getTableJSX}
                    </Paginator>
                        : <div className="flex-1 min-h-0 overflow-y-auto">{getTableJSX()}</div>
                }

            </ActivityFrame>
            {tableContext.tableData.length === 0 && !legendContext.tableLegendSelectable ? null : <div className="flex-shrink-0"><TableLegend /></div>}
            <FilterModal
                isOpen={showFilterModal}
                onClose={() => { setShowFilterModal(false) }}
                filterModalTrigger={filterModalTrigger} />
        </div>
    );
};

export default TableTab;
